import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { READS_FETCH_LIMIT } from '@constants/miscellaneous';
import { IReadsPost } from '@interfaces/reads';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class ReadsService {

  readsPostsSubject: BehaviorSubject<IReadsPost[]>;
  lastReadDate: BehaviorSubject<Date>;

  constructor(
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
  ) { }

  getReads(): Observable<IReadsPost[]> {
    if (this.readsPostsSubject) {
      return this.readsPostsSubject.asObservable();
    }
    try {
      this.readsPostsSubject = new BehaviorSubject<IReadsPost[]>(null);
      const query = this.firebaseService.getCollection('reads')
                                        .where('published', '==', true)
                                        .orderBy('order', 'desc')
                                        .limit(READS_FETCH_LIMIT);
      const parseReadsFn = (querySnapshot) => {
        return querySnapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        } as IReadsPost));
      };
      this.firebaseService.observeQuery(query, this.readsPostsSubject, parseReadsFn);
      return this.readsPostsSubject.asObservable();
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los articulos', false);
      throw err;
    }
  }

  getLastReadDate(): Observable<Date> {
    if (this.lastReadDate) {
      return this.lastReadDate.asObservable();
    }
    try {
      this.lastReadDate = new BehaviorSubject<Date>(null);
      const query = this.firebaseService.getCollection('reads')
                                        .where('published', '==', true)
                                        .orderBy('date', 'desc')
                                        .limit(1);
      const extractDateFromQuery = (querySnapshot) => querySnapshot.docs[0].data().date.toDate();
      this.firebaseService.observeQuery(query, this.lastReadDate, extractDateFromQuery);
      return this.lastReadDate.asObservable();
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener ultimos Racional Reads', false);
      throw err;
    }
  }

  async getUnreadCount(fromDate): Promise<number> {
    try {
      return this.firebaseService.getCollection('reads')
                                 .where('published', '==', true)
                                 .where('date', '>', fromDate)
                                 .orderBy('date', 'desc')
                                 .get().then((docs) => docs.size);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener ultimos Racional Reads', false);
      throw err;
    }
  }

  getMoreReads() {
    const lastCollectionReceived = this.readsPostsSubject.getValue();
    const readsCount = lastCollectionReceived.length;
    const lastOrderNumberReceived = lastCollectionReceived[readsCount - 1].order;
    try {
      const query = this.firebaseService.getCollection('reads')
                                        .where('published', '==', true)
                                        .orderBy('order', 'desc')
                                        .startAfter(lastOrderNumberReceived)
                                        .limit(READS_FETCH_LIMIT);
      const updateReadsFn = (querySnapshot) => {
        const currentReadsCount = this.readsPostsSubject.getValue().length;
        const newPosts = querySnapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        } as IReadsPost));
        if (currentReadsCount === readsCount) {
          // First Load
          this.readsPostsSubject.next([...lastCollectionReceived, ...newPosts]);
        } else if (currentReadsCount > readsCount) {
          // Next Loads
          const currentCollectionToUpdate = this.readsPostsSubject.getValue();
          for (let i = 0; i < newPosts.length; i++) {
            currentCollectionToUpdate[readsCount + i] = newPosts[i];
          }
          this.readsPostsSubject.next(currentCollectionToUpdate);
        }
      };
      this.firebaseService.onQuerySnapshot(query, updateReadsFn);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener mas articulos', false);
      throw err;
    }
  }

  async getReadInfo(id: string): Promise<IReadsPost> {
    if (this.readsPostsSubject) {
      const lastCollectionReceived = this.readsPostsSubject.getValue();
      const read = lastCollectionReceived.find(r => r.id === id);
      if (read) {
        return read as IReadsPost;
      }
    }
    try {
      const collection = this.firebaseService.getCollection('reads');
      const read = await this.firebaseService.getDocument(id, collection) as IReadsPost;
      return read as IReadsPost;
    } catch (err) {
      throw err;
    }
  }

  async getReadContent(id: string): Promise<string> {
    try {
      const collection = this.firebaseService.getCollection(`reads/${id}/content`);
      const contentObject = await this.firebaseService.getDocument('content', collection) as { content: string };
      return contentObject.content;
    } catch (err) {
      throw err;
    }
  }

  async getRead(id: string): Promise<IReadsPost> {
    const content = await this.getReadContent(id);
    const read = await this.getReadInfo(id);
    return { ...read, content } as IReadsPost;
  }
}
